import { type FC, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import type { LinkItem } from "src/http/api-types";
import { useContent } from "src/shared/components/content";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { useCreateQueryFn } from "src/shared/hooks/useCreateQueryFn";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import { createQueryKey } from "src/shared/utils/query-helpers";
import { QueryKeyPrefix } from "src/shared/utils/query/query-key-prefixes";
import { STALE_TIME_ONE_HOUR } from "src/shared/utils/query/retry-constants";
import type { Location } from "src/types/shared/cookies";
import { EmptyCarousel } from "src/website-apps/shared/components/carousel";
import { LocCarouselListSection } from "../LocCarouselListSection/LocCarouselListSection";
import { TeamsNearYouCarousel } from "./TeamsNearYouCarousel";
import { TeamsNearYouList } from "./TeamsNearYouList";

interface Props {
  categoryId: number | undefined;
  className?: string;
  setUserLocation: (location: Location) => void;
  userLocation?: Maybe<Location>;
}

/**
 * Carousel of Teams within a sports league grouping near the user's current preferred location
 * @see https://www.figma.com/file/oKyunzAbfxObdWw6kR5VyC/%5BGI%5D-Team-Logo-Template?type=design&node-id=1034-5090&mode=design&t=TmvwPshEyFkjBhRl-4
 */
export const TeamsNearYou: FC<Props> = ({ categoryId, className, setUserLocation, userLocation }) => {
  const createQueryFn = useCreateQueryFn();
  const storePageEvent = useStorePageEvent();
  const isMobile = !useMediaQuery(Breakpoint.Tablet);
  const content = useContent("cid$#3258295#"); // Teams near
  const { lt, lg } = userLocation ?? {};

  /**
   * Retrieve Teams Near You on Location Change
   */
  const {
    data: teams = [],
    error,
    isLoading: pending,
  } = useQuery<Maybe<LinkItem[]>>({
    queryKey: createQueryKey(QueryKeyPrefix.TEAMS_NEAR_YOU, { categoryId, lg, lt }),
    queryFn: createQueryFn({
      method: "GetSportsTeamsNearYou",
      params: [
        ["categoryId", categoryId ?? ""],
        ["userLongitude", lg ?? ""],
        ["userLatitude", lt ?? ""],
      ],
    }),
    enabled: Boolean(lg && lt),
    staleTime: STALE_TIME_ONE_HOUR, // 1 hour
  });

  /**
   * Page event handler for teams near you card click
   */
  const onCardClick = useCallback(() => {
    storePageEvent(PE_TYPE.CLICK, PE_KEYS.CATEGORY_GROUPING_TEAMS_NEAR_YOU_CARD_CLICK);
  }, [storePageEvent]);

  // Early exit if no data
  if (!teams?.length) {
    return null;
  }

  return (
    <LocCarouselListSection
      className={className}
      headerContent={content}
      placement="GroupingTeamsNearYou"
      setUserLocation={setUserLocation}
      userLocation={userLocation}
    >
      {!error && !pending && (
        <>
          {isMobile && <TeamsNearYouList onClick={onCardClick} teams={teams} />}
          {!isMobile && <TeamsNearYouCarousel id="TeamsNearYou" onClick={onCardClick} teams={teams} />}
        </>
      )}
      {pending && <EmptyCarousel noResult={false} placeHolderLineCount={1} size={isMobile ? 2 : 3} />}
    </LocCarouselListSection>
  );
};
